import axios from 'axios';

const API = axios.create({
  baseURL: '/api',
});

API.interceptors.request.use((config) => {
  const token = localStorage.getItem('ems_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

API.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 403) {
      const message = err.response?.data?.message || 'You do not currently have permission from your Organization Head or Organization Admin to access this feature.';
      window.dispatchEvent(new CustomEvent('ems:permission-denied', { detail: { message } }));
    }
    if (err.response?.status === 401) {
      localStorage.removeItem('ems_token');
      localStorage.removeItem('ems_user');
      window.dispatchEvent(new Event('ems:unauthorized'));
    }
    return Promise.reject(err);
  }
);

// Auth
export const registerUser         = (data) => API.post('/register', data);
export const loginUser            = (data) => API.post('/login', data);
export const getProfile           = ()     => API.get('/profile');
export const verifyEmail          = (data) => API.post('/verify-email', data);
export const resendVerification   = (data) => API.post('/resend-verification', data);

// Password Reset
export const forgotPassword       = (data) => API.post('/forgot-password', data);
export const verifyResetOtp       = (data) => API.post('/verify-reset-otp', data);
export const resetPassword        = (data) => API.post('/reset-password', data);
export const resendResetOtp       = (data) => API.post('/resend-reset-otp', data);

// Transactions
export const createTransaction = (data)   => API.post('/transaction', data);
export const getTransactions   = (params) => API.get('/transaction', { params });
export const getTransaction    = (id)     => API.get(`/transaction/${id}`);
export const getTransactionHistory = (id) => API.get(`/transaction/${id}/history`);
export const createTransactionHistory = (id, data) => API.post(`/transaction/${id}/history`, data);
export const getBalance        = (params) => API.get('/transaction/balance', { params });
export const getProjects       = ()       => API.get('/transaction/projects');
export const updateTransaction = (id, data) => API.put(`/transaction/${id}`, data);
export const deleteTransaction = (id)    => API.delete(`/transaction/${id}`);

// Project History API
export const getProjectHistory = (projectId, projectName, filterType) => {
  const params = {};
  if (projectId) params.project_id = projectId;
  if (projectName) params.project_name = projectName;
  if (filterType && filterType !== 'all') params.filter = filterType;
  return API.get('/transaction/history', { params });
};

export const getProjectHistoryByDateRange = (projectId, projectName, startDate, endDate) => {
  const params = {};
  if (projectId) params.project_id = projectId;
  if (projectName) params.project_name = projectName;
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  return API.get('/transaction/history/range', { params });
};

export const getProjectHistoryByMonth = (month, year, projectId, projectName) => {
  const params = {};
  if (projectId) params.project_id = projectId;
  if (projectName) params.project_name = projectName;
  return API.get(`/transaction/history/month/${month}/year/${year}`, { params });
};

export const getProjectHistoryAnalytics = (params) => API.get('/transaction/history/analytics', { params });

// Projects
export const createProject      = (data)   => API.post('/project', data);
export const getProjectsList    = async () => {
  const res = await API.get('/project');
  const root = res?.data;
  const list = Array.isArray(root?.data)
    ? root.data
    : Array.isArray(root?.projects)
      ? root.projects
      : Array.isArray(root)
        ? root
        : [];

  return {
    ...res,
    data: {
      ...(root && typeof root === 'object' ? root : {}),
      data: list,
    },
  };
};
export const getProjectById     = (id)     => API.get(`/project/${id}`);
export const updateProject      = (id, d)  => API.put(`/project/${id}`, d);
export const deleteProject      = (id)     => API.delete(`/project/${id}`);
export const getActiveProject   = ()       => API.get('/project/active');
export const setActiveProject   = (project_id) => API.put('/project/active', { project_id });

export const getProjectCategories = (projectId) => API.get(`/project/${projectId}/categories`);
export const createProjectCategory = (projectId, data) => API.post(`/project/${projectId}/categories`, data);
export const updateProjectCategory = (projectId, categoryId, data) => API.put(`/project/${projectId}/categories/${categoryId}`, data);
export const deleteProjectCategory = (projectId, categoryId) => API.delete(`/project/${projectId}/categories/${categoryId}`);

// Persons
export const getPersons = () => API.get('/person');
export const createPerson = (data) => API.post('/person', data);
export const updatePerson = (id, data) => API.put(`/person/${id}`, data);
export const deletePerson = (id) => API.delete(`/person/${id}`);

export default API;

// ─── Organizations ────────────────────────────────────────────────────────────
export const getMyOrganizations     = ()           => API.get('/organization');
export const getOrganization        = (id)         => API.get(`/organization/${id}`);
export const updateOrganization     = (id, data)   => API.put(`/organization/${id}`, data);
export const getOrgMembers          = (id, params) => API.get(`/organization/${id}/members`, { params });
export const updateMemberRole       = (id, uid, data) => API.put(`/organization/${id}/members/${uid}/role`, data);
export const removeMember           = (id, uid)    => API.delete(`/organization/${id}/members/${uid}`);
export const toggleMember           = (id, uid, data) => API.put(`/organization/${id}/members/${uid}/toggle`, data);
export const inviteMember           = (id, data)   => API.post(`/organization/${id}/invitations`, data);
export const getOrgInvitations      = (id)         => API.get(`/organization/${id}/invitations`);
export const joinByToken            = (data)       => API.post('/join-organization', data);
export const joinByCode             = (data)       => API.post('/join-organization/code', data);
export const validateInviteToken    = (token)      => API.get(`/validate-invite/${token}`);
export const switchOrganization     = (data)       => API.post('/switch-organization', data);
export const getOrgStats            = (id)         => API.get(`/organization/${id}/stats`);
export const getOrganizationPermissions = ()       => API.get('/organization/permissions');
export const getOrganizationRoles       = ()       => API.get('/organization/roles');
export const getOrgPermissionHierarchy  = ()       => API.get('/organization/permission-hierarchy');
export const getOrganizationRolePermissions = (role) => API.get('/organization/role-permissions', { params: { role } });
export const updateOrganizationRolePermissions = (data) => API.put('/organization/role-permissions', data);
export const getOrganizationUserPermissions = (userId) => API.get(`/organization/users/${userId}/permissions`);
export const updateOrganizationUserPermissions = (userId, data) => API.put(`/organization/users/${userId}/permissions`, data);

// ─── Super Admin – Organizations ─────────────────────────────────────────────
export const saGetOrganizations     = (params)     => API.get('/super-admin/organizations', { params });
export const saGetOrganization      = (id)         => API.get(`/super-admin/organizations/${id}`);
export const saUpdateOrgStatus      = (id, data)   => API.put(`/super-admin/organizations/${id}/status`, data);
export const saUpdateOrgPlan        = (id, data)   => API.put(`/super-admin/organizations/${id}/plan`, data);

// ─── Admin ────────────────────────────────────────────────────────────────────
export const adminGetStats      = ()           => API.get('/admin/stats');
export const adminGetUsers      = (params)     => API.get('/admin/users', { params });
export const adminGetUser       = (id)         => API.get(`/admin/users/${id}`);
export const adminUpdateUser    = (id, data)   => API.put(`/admin/users/${id}`, data);
export const adminDeleteUser    = (id)         => API.delete(`/admin/users/${id}`);
export const adminBlockUser     = (id, data)   => API.put(`/admin/users/${id}/block`, data);
export const adminResetPassword = (id, data)   => API.put(`/admin/users/${id}/password`, data);
export const adminGetLogs       = (params)     => API.get('/admin/logs', { params });
export const adminGetTransactions = (params)   => API.get('/admin/transactions', { params });

// ─── Super Admin ──────────────────────────────────────────────────────────────
export const superAdminLogin = (data) => API.post('/super-admin/auth/login', data);
export const superAdminLogout = () => API.post('/super-admin/auth/logout');
export const superAdminSession = () => API.get('/super-admin/auth/session');

export const superAdminDashboard = () => API.get('/super-admin/dashboard');
export const superAdminRevenueAnalytics = () => API.get('/super-admin/analytics/revenue');
export const superAdminUserGrowthAnalytics = () => API.get('/super-admin/analytics/user-growth');
export const superAdminFinancialSummary = () => API.get('/super-admin/analytics/financial-summary');

export const superAdminGetUsers = (params) => API.get('/super-admin/users', { params });
export const superAdminGetUser = (id) => API.get(`/super-admin/users/${id}`);
export const superAdminUpdateUserRole = (id, data) => API.put(`/super-admin/users/${id}/role`, data);
export const superAdminSuspendUser = (id, data) => API.put(`/super-admin/users/${id}/suspend`, data);
export const superAdminUpdateUser = (id, data) => API.put(`/super-admin/users/${id}`, data);
export const superAdminDeleteUser = (id) => API.delete(`/super-admin/users/${id}`);

export const superAdminGetAdmins = () => API.get('/super-admin/admins');
export const superAdminCreateAdmin = (data) => API.post('/super-admin/admins', data);
export const superAdminRemoveAdmin = (id) => API.delete(`/super-admin/admins/${id}`);
export const superAdminUpdateAdminPermissions = (id, data) => API.put(`/super-admin/admins/${id}/permissions`, data);
export const superAdminGetPermissions = () => API.get('/super-admin/permissions');
export const superAdminGetRolePermissions = (role) => API.get(`/super-admin/permissions/${role}`);

export const superAdminGetProjects = (params) => API.get('/super-admin/projects', { params });
export const superAdminUpdateProject = (id, data) => API.put(`/super-admin/projects/${id}`, data);
export const superAdminGetTransactions = (params) => API.get('/super-admin/transactions', { params });
export const superAdminUpdateTransaction = (id, data) => API.put(`/super-admin/transactions/${id}`, data);
export const superAdminExportTransactions = (params) => API.get('/super-admin/transactions/export', { params });

export const superAdminGetAuditLogs = (params) => API.get('/super-admin/logs/audit', { params });
export const superAdminGetActivityLogs = (params) => API.get('/super-admin/logs/activity', { params });
export const superAdminGetLoginHistory = (params) => API.get('/super-admin/logs/login-history', { params });
export const superAdminGetSecurityEvents = (params) => API.get('/super-admin/logs/security-events', { params });
export const superAdminCreateSecurityEvent = (data) => API.post('/super-admin/security-events', data);

export const superAdminGetSettings = (params) => API.get('/super-admin/settings', { params });
export const superAdminUpdateSettings = (data) => API.put('/super-admin/settings', data);

export const superAdminGetNotifications = (params) => API.get('/super-admin/notifications', { params });
export const superAdminCreateNotification = (data) => API.post('/super-admin/notifications', data);
export const superAdminMarkNotificationRead = (id) => API.put(`/super-admin/notifications/${id}/read`);
